import { z } from "zod";

const nonEmptyString = z.string().trim().min(1);
const uuidString = z.uuid();

export const cursorSchema = z
  .string()
  .regex(/^(0|[1-9]\d*)$/, "cursor must be an unsigned decimal string");

export const pageCursorSchema = z
  .string()
  .min(40)
  .max(1_024)
  .regex(
    /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/,
    "page cursor must be an opaque signed token",
  );

const timestampSchema = z.iso.datetime({ offset: true });

const mediaUrlSchema = z
  .string()
  .regex(
    /^\/api\/v1\/media\/[A-Za-z0-9._~-]+$/,
    "media URL must be an exact same-origin media route",
  );

export const modelSummarySchema = z
  .object({
    modelId: nonEmptyString,
    readOnly: z.boolean(),
    readinessReason: nonEmptyString,
  })
  .strict();

export const modelRegistryViewSchema = z
  .object({
    models: z.array(modelSummarySchema),
  })
  .strict()
  .superRefine((registry, context) => {
    const seen = new Set<string>();
    for (const model of registry.models) {
      if (seen.has(model.modelId)) {
        context.addIssue({
          code: "custom",
          message: "model registry contains a duplicate modelId",
          path: ["models"],
        });
        return;
      }
      seen.add(model.modelId);
    }
  });

export const chatSummarySchema = z
  .object({
    chatId: uuidString,
    fanId: uuidString,
    modelId: nonEmptyString,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    lastItemPreview: z.string().nullable(),
    lastItemAt: timestampSchema.nullable(),
    readOnly: z.boolean(),
    readinessReason: nonEmptyString,
  })
  .strict();

const timelineBase = {
  itemId: uuidString,
  chatId: uuidString,
  cursor: cursorSchema,
  timestamp: timestampSchema,
};

export const fanMessageTimelineItemSchema = z
  .object({
    ...timelineBase,
    kind: z.literal("fan_message.v1"),
    text: nonEmptyString,
  })
  .strict();

export const modelMessageTimelineItemSchema = z
  .object({
    ...timelineBase,
    kind: z.literal("model_message.v1"),
    text: nonEmptyString,
  })
  .strict();

export const mediaPresentationSchema = z
  .object({
    mediaId: uuidString,
    presentation: z.enum(["photo", "video_preview"]),
    mediaUrl: mediaUrlSchema,
    alt: nonEmptyString,
  })
  .strict();

export const mediaTimelineItemSchema = z
  .object({
    ...timelineBase,
    kind: z.literal("media.v1"),
    ...mediaPresentationSchema.shape,
  })
  .strict();

export const offerTimelineItemSchema = z
  .object({
    ...timelineBase,
    kind: z.literal("offer.v1"),
    offerId: uuidString,
    price: z
      .string()
      .regex(/^(?:0+(?:\.0*)?|(?:0|[1-9]\d*)(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/),
    currency: z.literal("USD"),
    purchased: z.boolean(),
    media: mediaPresentationSchema.nullable(),
  })
  .strict();

export const purchaseTimelineItemSchema = z
  .object({
    ...timelineBase,
    kind: z.literal("purchase.v1"),
    offerId: uuidString,
  })
  .strict();

export const canonicalStageSchema = z.enum([
  "accepted",
  "platform_processing",
  "delivered",
  "blocked",
  "failed",
]);

export const statusTimelineItemSchema = z
  .object({
    ...timelineBase,
    kind: z.literal("status.v1"),
    stage: canonicalStageSchema,
    reasonCode: nonEmptyString.nullable(),
    message: nonEmptyString.nullable(),
  })
  .strict();

export const timelineItemSchema = z.discriminatedUnion("kind", [
  fanMessageTimelineItemSchema,
  modelMessageTimelineItemSchema,
  mediaTimelineItemSchema,
  offerTimelineItemSchema,
  purchaseTimelineItemSchema,
  statusTimelineItemSchema,
]);

export const chatViewSchema = chatSummarySchema
  .extend({
    timeline: z.array(timelineItemSchema).max(200),
    timelineNextBefore: pageCursorSchema.nullable(),
  })
  .strict();

export const workspaceViewSchema = z
  .object({
    models: z.array(modelSummarySchema),
    chats: z.array(chatSummarySchema).max(100),
    chatsNextBefore: pageCursorSchema.nullable(),
    activeChat: chatViewSchema.nullable(),
    cursor: cursorSchema,
    csrfToken: nonEmptyString,
  })
  .strict();

export const loginResponseSchema = z
  .object({ authenticated: z.literal(true) })
  .strict();

export const actionReceiptSchema = z
  .object({
    clientActionId: z.uuid(),
    chatId: uuidString,
    status: z.enum(["accepted", "replayed"]),
    crmEventId: nonEmptyString,
    timelineCursor: cursorSchema,
  })
  .strict();

export const timelineDeltaSchema = z
  .object({
    cursor: cursorSchema,
    item: timelineItemSchema,
  })
  .strict();

export const canonicalErrorSchema = z
  .object({
    code: nonEmptyString,
    category: nonEmptyString,
    retryable: z.boolean(),
    safeToRetryWithSameIdempotencyKey: z.boolean(),
    message: nonEmptyString,
    details: z.record(z.string(), z.unknown()),
  })
  .strict();

export const errorEnvelopeSchema = z
  .object({ error: canonicalErrorSchema })
  .strict();

export const timelineErrorEventSchema = z
  .object({ error: canonicalErrorSchema })
  .strict();

export const chatActionSchema = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("message.v1"),
      clientActionId: z.uuid(),
      text: z.string().trim().min(1).max(10_000),
    })
    .strict(),
  z
    .object({
      kind: z.literal("purchase.v1"),
      clientActionId: z.uuid(),
      offerId: uuidString,
    })
    .strict(),
]);

export type ModelSummary = z.infer<typeof modelSummarySchema>;
export type ModelRegistryView = z.infer<typeof modelRegistryViewSchema>;
export type ChatSummary = z.infer<typeof chatSummarySchema>;
export type ChatView = z.infer<typeof chatViewSchema>;
export type TimelineItem = z.infer<typeof timelineItemSchema>;
export type MediaTimelineItem = z.infer<typeof mediaTimelineItemSchema>;
export type MediaPresentation = z.infer<typeof mediaPresentationSchema>;
export type OfferTimelineItem = z.infer<typeof offerTimelineItemSchema>;
export type CanonicalStage = z.infer<typeof canonicalStageSchema>;
export type CanonicalError = z.infer<typeof canonicalErrorSchema>;
export type WorkspaceView = z.infer<typeof workspaceViewSchema>;
export type ActionReceipt = z.infer<typeof actionReceiptSchema>;
export type TimelineDelta = z.infer<typeof timelineDeltaSchema>;
export type ChatAction = z.infer<typeof chatActionSchema>;
